import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CITY_BY_SLUG, CITIES, CITY_SERVICES } from "@/lib/cities";
import { SERVICES, SERVICE_BY_SLUG } from "@/lib/services";
import { isKeptCityService } from "@/lib/kept-city-services";
import { comboHref, uniqueByHref } from "@/lib/combo-links";
import { BUSINESS } from "@/lib/business";
import { PhoneButton } from "@/components/site/PhoneButton";
import { QuoteForm } from "@/components/site/QuoteForm";
import { TrustBar } from "@/components/site/TrustBar";
import { Testimonials } from "@/components/site/Testimonials";
import { FAQ } from "@/components/site/FAQ";
import { CTABand } from "@/components/site/CTABand";
import { faqSchema, breadcrumbSchema, serviceSchema } from "@/components/site/SchemaJsonLd";
import { Check, MapPin, Clock, ShieldCheck, Phone } from "lucide-react";
import cityHero from "@/assets/city-hero.webp";
import cityVan from "@/assets/city-van.webp";
import cityVan1024 from "@/assets/city-van-1024.webp";
import cityVan640 from "@/assets/city-van-640.webp";

export const Route = createFileRoute("/areas-served/$city/")({
  loader: ({ params }) => {
    const city = CITY_BY_SLUG[params.city];
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData, params }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "City not found" }] };
    // Phoenix hub targets neighborhood/ZIP intent so it doesn't compete with the homepage.
    const title = city.slug === "phoenix"
      ? "Plumber in Phoenix Neighborhoods & ZIP Codes | Same-Day"
      : `Plumber in ${city.name}, AZ | Phoenix Plumbing Co.`;
    const desc = `Licensed emergency plumber in ${city.name}, AZ. Same-day response in ${city.responseTime}. Drains, water heaters, leaks. Call ${BUSINESS.phone}.`.slice(0, 160);
    const SITE_URL = "https://www.phoenixplumbing.co";
    const path = `${SITE_URL}/areas-served/${params.city}`;
    const faqs = cityFaqs(city.name, city.responseTime, city.localIssue);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: path },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: path },
        { rel: "preload", as: "image", href: cityHero, fetchpriority: "high" } as any,
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceSchema(`Plumbing Services in ${city.name}, AZ`, desc, city.name)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqSchema(faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", url: "/" },
              { name: "Areas Served", url: "/areas-served" },
              { name: city.name, url: `/areas-served/${params.city}` },
            ]),
          ),
        },
      ],
    };
  },
  component: CityPage,
});

function cityFaqs(cityName: string, responseTime: string, localIssue: string) {
  return [
    {
      q: `Do you serve all of ${cityName}, AZ?`,
      a: `Yes — Phoenix Plumbing Co. covers every ZIP code in ${cityName} and the surrounding neighborhoods. We dispatch licensed plumbers to ${cityName} during business hours.`,
    },
    {
      q: `How fast can a plumber get to my ${cityName} home?`,
      a: `Most ${cityName} service calls receive a licensed plumber within ${responseTime}, including nights and weekends. Active emergencies are dispatched first.`,
    },
    {
      q: `What plumbing problems are most common in ${cityName}?`,
      a: `${localIssue} We see these issues regularly and arrive with the right parts to fix them on the first visit.`,
    },
    {
      q: `Are you licensed and insured to work in ${cityName}?`,
      a: `Yes. Phoenix Plumbing Co. is a licensed, bonded, and insured Arizona ROC contractor with $2M general liability. We pull permits in ${cityName} when code requires them.`,
    },
    {
      q: `Do you offer free estimates in ${cityName}?`,
      a: `Quotes on installations and replacements are always free in ${cityName}. Repair diagnostics are a small flat fee that is credited toward the repair if you approve the work.`,
    },
    {
      q: `Do you charge extra for emergency or after-hours service in ${cityName}?`,
      a: `We charge a modest after-hours dispatch fee for nights, weekends, and holidays in ${cityName}, but the per-hour labor rate stays the same and every job is quoted upfront before work begins.`,
    },
  ];
}

function CityPage() {
  const { city } = Route.useLoaderData();
  const otherCities = CITIES.filter((c) => c.slug !== city.slug).slice(0, 8);
  const faqs = cityFaqs(city.name, city.responseTime, city.localIssue);
  return (
    <>
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-secondary to-secondary/90 text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${cityHero})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/85 to-secondary/45" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:py-16 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-accent font-bold text-sm uppercase tracking-widest flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Serving {city.name}, Arizona
            </p>
            <h1 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight">
              {city.slug === "phoenix"
                ? "Phoenix Neighborhoods We Serve — Arcadia, Ahwatukee, North Phoenix & More"
                : `Plumber in ${city.name}, AZ — Licensed, Insured`}
            </h1>
            <p className="mt-5 text-lg text-secondary-foreground/90 leading-relaxed">
              {city.blurb} A licensed Phoenix Plumbing Co. technician is on the way to your {city.name} home in{" "}
              <strong className="text-accent">{city.responseTime}</strong>. Call{" "}
              <a href={BUSINESS.phoneHref} className="font-extrabold text-accent hover:underline">{BUSINESS.phone}</a>{" "}
              for upfront pricing and same-day service.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <PhoneButton size="lg" />
              <a href="#quote" className="inline-flex items-center justify-center rounded-md border-2 border-accent px-6 py-4 text-base font-extrabold text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
                Get Free Quote
              </a>
            </div>
            <div className="mt-6"><TrustBar dark /></div>
          </div>
          <div id="quote" className="lg:mt-2">
            <QuoteForm defaultService={`Plumbing in ${city.name}`} />
          </div>
        </div>
      </section>

      {/* Quick stats */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-6 grid gap-4 sm:grid-cols-3">
          <Stat icon={<Clock className="h-5 w-5" />} label="Response Time" value={city.responseTime} />
          <Stat icon={<ShieldCheck className="h-5 w-5" />} label="Licensed" value={BUSINESS.license} />
          <Stat icon={<Phone className="h-5 w-5" />} label="Dispatch" value={BUSINESS.phone} href={BUSINESS.phoneHref} />
        </div>
      </section>

      {/* Intro + local issue */}
      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-2 items-start">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            {city.name}'s Trusted Local Plumber Since {BUSINESS.founded}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-foreground">
            Phoenix Plumbing Co. has been answering {city.name} plumbing calls since {BUSINESS.founded} — repairing leaks,
            replacing water heaters, clearing drains, and remodeling kitchens and bathrooms across the city. Every
            technician dispatched to {city.name} is a licensed, background-checked plumber driving a fully stocked
            truck, which means most repairs are finished on the first visit.
          </p>
          <div className="mt-6 rounded-xl border-l-4 border-accent bg-muted/40 p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              What we see most in {city.name}
            </div>
            <p className="mt-2 text-foreground font-medium">{city.localIssue}</p>
          </div>
          <h3 className="mt-8 text-xl font-bold text-secondary">Neighborhoods we cover in {city.name}</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {city.neighborhoods.map((n: string) => (
              <li key={n} className="rounded-full bg-secondary/10 text-secondary px-3 py-1 text-sm font-semibold">
                {n}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-border">
          <img srcSet={`${cityVan640} 640w, ${cityVan1024} 1024w, ${cityVan} 1264w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px" width={1264} height={848} decoding="async" src={cityVan} alt={`Phoenix Plumbing Co. service van in ${city.name}, AZ`} className="w-full h-auto block" loading="lazy" />
        </div>
      </section>

      {/* Local Context — deep on-page SEO for the city */}
      <section className="bg-card border-y border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-[1.4fr_1fr] items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
              Plumbing in {city.name}, AZ — what makes this city different
            </h2>
            <p className="mt-4 text-foreground leading-relaxed">{city.localContext}</p>
            <p className="mt-4 text-foreground leading-relaxed">
              <strong className="text-secondary">{city.name} water:</strong> {city.waterUtility}
            </p>
            <p className="mt-4 text-foreground leading-relaxed">
              Whether you're near {city.landmarks.slice(0, 3).join(", ")}, or anywhere else in {city.name}, our licensed
              plumbers arrive in {city.responseTime}. Same upfront pricing, same written workmanship guarantee, every job.
            </p>
            {city.slug === "phoenix" && (
              <p className="mt-4 text-foreground leading-relaxed">
                For permitted work, remodels, and new builds we act as your licensed{" "}
                <Link to="/phoenix-plumbing-contractors" className="text-primary font-semibold underline">
                  plumbing contractors in Phoenix
                </Link>
                , pulling the permit and meeting the inspector at every phase. And because nearly every home here is
                slab-on-grade, our{" "}
                <Link to="/slab-leak-detection-and-repair-phoenix-az" className="text-primary font-semibold underline">
                  slab leak detection
                </Link>{" "}
                crew stays busy year-round with warm floors and unexplained water-bill spikes.
              </p>
            )}

            <h3 className="mt-8 text-xl font-bold text-secondary">
              {city.name} ZIP codes we serve
            </h3>
            <ul className="mt-3 flex flex-wrap gap-2">
              {city.zips.map((z: string) => (
                <li key={z} className="rounded-md bg-muted text-secondary px-2.5 py-1 text-sm font-mono font-semibold">
                  {z}
                </li>
              ))}
            </ul>

            <h3 className="mt-8 text-xl font-bold text-secondary">
              Landmarks near our {city.name} jobs
            </h3>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {city.landmarks.map((l: string) => (
                <li key={l} className="flex gap-2 text-foreground">
                  <MapPin className="h-4 w-4 text-primary mt-1 shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="rounded-2xl border border-border bg-muted/40 p-6 shadow-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Common Plumbing Services in {city.name}
            </div>
            <p className="mt-3 text-sm text-foreground">
              Phoenix Plumbing Co. helps homeowners and businesses in {city.name} with common
              plumbing problems and planned plumbing upgrades. Our services include water heater
              repair, drain cleaning, leak detection, sewer line repair, toilet repair, faucet
              installation, garbage disposal repair, gas line plumbing, and emergency plumbing
              service.
            </p>
            <div className="mt-5">
              <PhoneButton className="w-full justify-center" />
            </div>
            <p className="mt-3 text-xs text-muted-foreground text-center">
              {city.name} dispatch • {city.responseTime} typical arrival
            </p>
          </aside>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
            Plumbing Services Available in {city.name}, AZ
          </h2>
          <p className="mt-3 text-foreground max-w-3xl">
            Whatever your {city.name} home or business needs, we handle it. Every service below is dispatched daily
            across {city.name} with upfront pricing and a written workmanship guarantee.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}` as any}
                className="group rounded-xl border border-border bg-card p-5 hover:border-primary hover:shadow-md transition-all"
              >
                <div className="font-bold text-secondary group-hover:text-primary">
                  {s.name} in {city.name}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{s.short}</p>
                <div className="mt-3 text-xs font-semibold text-primary">View details →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Top demand callouts */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
          Top Requested {city.name} Plumbing Jobs
        </h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {uniqueByHref(CITY_SERVICES, (cs) => comboHref(city.slug, cs.slug)).map((cs) => {
            const svc = SERVICE_BY_SLUG[cs.parentService];
            // Kept combos link to the dedicated city page; everything else
            // links to the root service page (consolidation anti-doorway rule).
            const kept = isKeptCityService(city.slug, cs.slug);
            const cardBody = (
              <>
                <div className="flex items-center gap-2 text-accent-ink text-xs uppercase tracking-widest font-bold">
                  <Check className="h-4 w-4" /> Same-day in {city.name}
                </div>
                <div className="mt-2 font-extrabold text-secondary text-lg">
                  {cs.name} — {city.name}, AZ
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{svc?.short}</p>
                {svc?.priceRange && (
                  <div className="mt-3 text-xs font-semibold text-secondary/80">{svc.priceRange}</div>
                )}
              </>
            );
            return kept ? (
              <Link
                key={cs.slug}
                to="/areas-served/$city/$service"
                params={{ city: city.slug, service: cs.slug }}
                className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors"
              >
                {cardBody}
              </Link>
            ) : (
              <Link
                key={cs.slug}
                to={`/${cs.parentService}` as any}
                className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors"
              >
                {cardBody}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Why us */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold">
            Why {city.name} Homeowners Call Phoenix Plumbing Co.
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Licensed & Insured", d: `AZ ROC licensed, bonded, $2M insured — permits pulled in ${city.name}.` },
              { t: `${city.responseTime} Response`, d: `Most ${city.name} calls answered live and dispatched within the hour.` },
              { t: "Upfront Pricing", d: "Flat quotes before any work begins. No hourly surprises." },
              { t: "Written Guarantee", d: "Every repair and install backed in writing — parts and labor." },
            ].map((b) => (
              <div key={b.t} className="rounded-xl border border-secondary-foreground/10 bg-secondary-foreground/5 p-5">
                <div className="font-extrabold text-accent">{b.t}</div>
                <p className="mt-2 text-sm text-secondary-foreground/85">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-extrabold text-secondary">
          Reviews from {city.name} & Valley Customers
        </h2>
        <div className="mt-6"><Testimonials count={3} city={city.name} /></div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-secondary mb-6">
            {city.name} Plumbing FAQ
          </h2>
          <FAQ items={faqs} />
        </div>
      </section>

      {/* Other cities */}
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-xl font-extrabold text-secondary mb-4">Other Cities We Serve</h2>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {otherCities.map((c) => (
            <Link
              key={c.slug}
              to="/areas-served/$city"
              params={{ city: c.slug }}
              className="rounded-lg border border-border bg-card p-4 hover:border-primary transition-colors"
            >
              <div className="font-bold text-secondary">Plumber in {c.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.responseTime} response</div>
            </Link>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}

function Stat({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href?: string }) {
  const inner = (
    <div className="flex items-center gap-3">
      <div className="rounded-full bg-primary/10 text-primary p-2">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
        <div className="font-extrabold text-secondary">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : <div>{inner}</div>;
}